// Market depth for BOTH legs of one opportunity, laid out like Kite's depth window:
// Bid (Price · Orders · Qty) | Offer (Price · Orders · Qty), five levels each, plus totals.
// Read straight from the options feed's latest FULL tick for each contract.
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import optionsApi, { optionsError, OPTIONS_HEDGE_ENABLED } from "@/lib/optionsApi";
import TradeTicket from "@/components/options/TradeTicket";
import SellTicket from "@/components/options/SellTicket";
import PairValuation from "@/components/options/PairValuation";
import { age, CapitalPanel, EconomicsNote, LegStatus, ltpWarningText, px, qty } from "@/components/options/OptionsCommon";
import { visibleInterval } from "@/lib/poll";

// markets whose pairs can be traded by Options Hedging (NFO stock options, MCX commodity options)
export const HEDGE_MARKETS = ["NFO", "MCX"];

// IST wall-clock time of an ISO instant, to the second (what Kite's depth window shows)
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }) : "—");

function sum(levels) {
  return (levels || []).reduce((a, l) => a + (l?.quantity || 0), 0);
}

function DepthTable({ title, symbol, rec, leg, testId }) {
  const buy = rec?.depth?.buy || [];
  const sell = rec?.depth?.sell || [];
  const rows = [0, 1, 2, 3, 4];
  const cell = "px-2 py-1 text-right mono";
  return (
    <div className="term-panel rounded-sm p-3 space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold">{title} · <span className="mono">{symbol}</span></div>
        {leg && <div className="text-[10px] text-[var(--term-muted)]">LTP-mode <LegStatus value={leg.ltp_status} /> · OFFER-mode <LegStatus value={leg.offer_status} /></div>}
      </div>
      <table className="w-full text-xs border-collapse">
        <thead className="text-[var(--term-muted)]">
          <tr>
            <th className="px-2 py-1 text-right text-[#2563EB]">Bid</th><th className="px-2 py-1 text-right">Orders</th><th className="px-2 py-1 text-right">Qty.</th>
            <th className="px-2 py-1 text-right text-[#FF3B30]">Offer</th><th className="px-2 py-1 text-right">Orders</th><th className="px-2 py-1 text-right">Qty.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((i) => (
            <tr key={i} className="border-t border-[var(--term-border)]" data-testid={`${testId}-level-${i + 1}`}>
              <td className={`${cell} text-[#2563EB]`}>{px(buy[i]?.price || null)}</td>
              <td className={cell}>{qty(buy[i]?.price ? buy[i]?.orders : null)}</td>
              <td className={cell}>{qty(buy[i]?.price ? buy[i]?.quantity : null)}</td>
              <td className={`${cell} text-[#FF3B30]`}>{px(sell[i]?.price || null)}</td>
              <td className={cell}>{qty(sell[i]?.price ? sell[i]?.orders : null)}</td>
              <td className={cell}>{qty(sell[i]?.price ? sell[i]?.quantity : null)}</td>
            </tr>
          ))}
          <tr className="border-t border-[var(--term-border-strong)] font-bold">
            <td className="px-2 py-1 text-right" colSpan={2}>Total</td><td className={cell}>{qty(sum(buy))}</td>
            <td className="px-2 py-1 text-right" colSpan={2}>Total</td><td className={cell}>{qty(sum(sell))}</td>
          </tr>
        </tbody>
      </table>
      <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-[11px] mono">
        <span>LTP <b>{px(rec?.ltp)}</b></span>
        <span>Best bid <b>{px(rec?.bid)}</b> × {qty(rec?.bid_qty)}</span>
        <span>Best offer <b>{px(rec?.ask)}</b> × {qty(rec?.ask_qty)}</span>
        <span>OI {qty(rec?.oi)}</span>
        <span>Volume {qty(rec?.volume)}</span>
        <span>Avg {px(rec?.average_traded_price)}</span>
        <span className="col-span-3 text-[var(--term-muted)]">
          last trade {clock(rec?.last_trade_time)} ({age(rec?.trade_age_s)} ago) · exchange ts {clock(rec?.exchange_timestamp)} ·
          received {clock(rec?.arrival)} ({age(rec?.tick_age_s)} ago) · book unchanged {age(rec?.depth_unchanged_for_s)}
        </span>
      </div>
    </div>
  );
}

/** Depth of arbitrary contracts of the caller's feed (Option Chain / Validation), even when no pair
 *  qualifies. `legs` = [{ token, title, leg? }] where `leg` optionally carries ltp/offer status. */
export function ContractDepthDrawer({ legs, heading, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const key = (legs || []).map((l) => l.token).join(",");
  useEffect(() => {
    if (!key) return undefined;
    const load = () => optionsApi.get("/depth", { params: { tokens: key } })
      .then((r) => { setD(r.data); setErr(null); }).catch((e) => setErr(optionsError(e)));
    load();
    return visibleInterval(load, 1500);
  }, [key]);
  if (!key) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="w-[760px] max-w-full h-full overflow-auto bg-[var(--term-bg)] border-l border-[var(--term-border-strong)] p-4 space-y-3"
        onClick={(e) => e.stopPropagation()} data-testid="options-contract-depth-drawer">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold">Market depth · {heading}</div>
          <button onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        {err && <div className="text-xs text-[#FF3B30]">{err}</div>}
        {(d?.legs || []).map((x, i) => {
          const meta = legs.find((l) => l.token === x.contract.instrument_token) || legs[i] || {};
          return <DepthTable key={x.contract.instrument_token} title={meta.title || x.contract.option_type}
            symbol={x.contract.tradingsymbol} rec={x.record} leg={meta.leg} testId={`depth-${x.contract.option_type.toLowerCase()}`} />;
        })}
        {d && d.legs.some((x) => !x.subscribed) && <div className="text-[11px] text-[#b38f00]">A contract outside the subscription band has no live data.</div>}
      </div>
    </div>
  );
}

export default function DepthDrawer({ pairId, focus, mode, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // TRADE opens the trader's ticket with the market detail folded; a row click is analysis
  const [ticket, setTicket] = useState(focus === "hedge");
  const [showMarket, setShowMarket] = useState(focus !== "hedge");
  useEffect(() => { setTicket(focus === "hedge"); setShowMarket(focus !== "hedge"); }, [focus, pairId]);
  const hedgeRef = useRef(null);
  const loaded = !!d;
  // TRADE: once the pair has loaded, bring "Options Hedge · Execution eligibility" into view
  useEffect(() => {
    if (focus === "hedge" && loaded && hedgeRef.current) {
      hedgeRef.current.scrollIntoView?.({ block: "start", behavior: "smooth" });
      hedgeRef.current.focus?.({ preventScroll: true });
    }
  }, [focus, loaded, pairId]);
  useEffect(() => { setD(null); }, [pairId]);
  useEffect(() => {
    if (!pairId) return undefined;
    const load = () => optionsApi.get(`/opportunities/${encodeURIComponent(pairId)}`)
      .then((r) => { setD(r.data); setErr(null); }).catch((e) => setErr(optionsError(e)));
    load();
    return visibleInterval(load, 1500);
  }, [pairId]);
  if (!pairId) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="w-[760px] max-w-full h-full overflow-auto bg-[var(--term-bg)] border-l border-[var(--term-border-strong)] p-4 space-y-3"
        onClick={(e) => e.stopPropagation()} data-testid="options-depth-drawer">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold">{ticket ? "Trade" : "Market depth"} · {d?.underlying} {d?.expiry}</div>
          <button onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        {err && <div className="text-xs text-[#FF3B30]">{err} (the pair may have stopped qualifying)</div>}
        {d && (
          <>
            <div ref={hedgeRef} tabIndex={-1} className="outline-none scroll-mt-2" data-testid="options-hedge-anchor">
              {d.direction === "SELL"
                ? (OPTIONS_HEDGE_ENABLED && HEDGE_MARKETS.includes(d.market) && d.ce?.instrument_token && d.pe?.instrument_token
                    ? (ticket
                        ? <SellTicket ceToken={d.ce.instrument_token} peToken={d.pe.instrument_token} pairId={pairId} pair={d} />
                        : <button className="px-3 py-1.5 rounded-sm text-xs font-bold text-[#B45309] border border-[#B45309]/50 hover:bg-[#B45309]/10"
                            onClick={() => setTicket(true)} data-testid="options-sell-this-pair">Sell this pair</button>)
                    : <div className="term-panel rounded-sm p-3 text-xs text-[var(--term-muted)]" data-testid="options-sell-ticket-unavailable">
                        SELL OPTIONS trading is available for NFO / MCX pairs when Options Hedging is enabled on this build.</div>)
                : OPTIONS_HEDGE_ENABLED && HEDGE_MARKETS.includes(d.market) && d.ce?.instrument_token && d.pe?.instrument_token
                ? (ticket
                    ? <TradeTicket ceToken={d.ce.instrument_token} peToken={d.pe.instrument_token} pairId={pairId} pair={d} defaultMode={mode} />
                    : <button className="px-3 py-1.5 rounded-sm text-xs font-bold text-[#2563EB] border border-[#2563EB]/50 hover:bg-[#2563EB]/10"
                        onClick={() => setTicket(true)} data-testid="options-trade-this-pair">Trade this pair</button>)
                : focus === "hedge" && <div className="term-panel rounded-sm p-3 text-xs text-[var(--term-muted)]" data-testid="options-hedge-unavailable">
                    Trading is available for NFO pairs when Options Hedging is enabled on this build.</div>}
            </div>
            {!ticket && d.valuation && <PairValuation v={d.valuation} title="Pair valuation (scanner, live quotes)" testPrefix="drawer-valuation"
              entryCharges={d.capital?.status === "OK" ? d.capital?.charges_total : null} />}
            {ticket && <button className="flex items-center gap-1 text-[11px] text-[var(--term-muted)] hover:text-[var(--term-primary)]"
              onClick={() => setShowMarket(!showMarket)} data-testid="options-market-toggle">
              {showMarket ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Market depth and broker capital</button>}
            {showMarket && <>
            <div className="term-panel rounded-sm p-3 grid grid-cols-2 gap-2 text-xs mono">
              <span>Underlying {d.underlying_symbol} <b>{px(d.underlying_ltp)}</b> ({age(d.spot_age_s)})</span>
              <span>{d.market} · strike diff <b>{px(d.strike_diff)}</b></span>
              <span className="col-span-2"><EconomicsNote e={d.economics} /></span>
              {d.direction === "SELL" && <span className="col-span-2" data-testid="options-drawer-sell">SELL: credit {px(d.SELL?.credit)} · costs {px(d.SELL?.costs)} · net {px(d.SELL?.rank_net_credit)} · required {d.SELL?.required == null ? "— (no protection filter)" : px(d.SELL.required)} · break-evens {px(d.SELL?.lower_be)} / {px(d.SELL?.upper_be)} · CE sell VWAP {px(d.ce?.sell_vwap)} (LIMIT {px(d.ce?.sell_limit)}) · PE sell VWAP {px(d.pe?.sell_vwap)} (LIMIT {px(d.pe?.sell_limit)})</span>}
              {d.direction !== "SELL" && <>
              <span>LTP mode: combined {px(d.LTP?.combined)} → opp/unit <b>{px(d.LTP?.opp)}</b> · per lot {px(d.LTP?.opp_lot)} · entry/lot {px(d.LTP?.entry_value_lot)} · {d.LTP?.reason}</span>
              <span>OFFER mode: combined {px(d.OFFER?.combined)} → opp/unit <b>{px(d.OFFER?.opp)}</b> · per lot {px(d.OFFER?.opp_lot)} · entry/lot {px(d.OFFER?.entry_value_lot)} · {d.OFFER?.reason}</span>
              </>}
              <span>CE {d.ce?.tradingsymbol} bid {px(d.ce?.bid)} × {qty(d.ce?.bid_qty)} · offer {px(d.ce?.ask)} × {qty(d.ce?.ask_qty)} · OI {qty(d.ce?.oi)}</span>
              <span>PE {d.pe?.tradingsymbol} bid {px(d.pe?.bid)} × {qty(d.pe?.bid_qty)} · offer {px(d.pe?.ask)} × {qty(d.pe?.ask_qty)} · OI {qty(d.pe?.oi)}</span>
              {(d.LTP?.warnings || []).length > 0 &&
                <span className="col-span-2 text-[#B45309]" data-testid="options-drawer-ltp-warnings">WARNING: {ltpWarningText(d.LTP.warnings, d)}</span>}
            </div>
            <CapitalPanel c={d.capital} busy={busy} onRefresh={async () => {
              setBusy(true);
              try { const r = await optionsApi.post(`/opportunities/${encodeURIComponent(pairId)}/margin`); setD((x) => ({ ...x, capital: r.data.capital })); }
              catch (e) { toast.error(optionsError(e)); } finally { setBusy(false); }
            }} />
            <DepthTable title="CALL (CE)" symbol={d.ce?.tradingsymbol} rec={d.depth?.ce} leg={d.ce} testId="depth-ce" />
            <DepthTable title="PUT (PE)" symbol={d.pe?.tradingsymbol} rec={d.depth?.pe} leg={d.pe} testId="depth-pe" />
            <div className="text-[10px] text-[var(--term-muted)]">
              Depth refreshes every 1.5 s from the dedicated options feed. Entry prices: LTP mode uses LTP, OFFER mode uses the
              best offer. Bid is shown for liquidity / future exit monitoring only and never affects the entry opportunity.
            </div>
            </>}
          </>
        )}
      </div>
    </div>
  );
}
